import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  formatMoney,
  quantity as toQuantity,
  type Locale,
  type SpecificationGroup,
  type ToppingTemplate,
} from "@tovi/shared";
import { Badge, SearchInput, useI18n, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { useTill } from "@/lib/use-till.ts";
import { subscribeToStoreEvents } from "@/lib/events.ts";
import { CartPane } from "@/components/cart.tsx";
import { ItemSheet } from "@/components/item-sheet.tsx";
import { useCart, type CartLine } from "@/lib/cart.ts";
import { usePress } from "@/lib/use-press.ts";
import { SettleDialog } from "@/components/settle.tsx";
import { DiscountDialog } from "@/components/discount.tsx";
import { LineActionsDialog } from "@/components/line-actions.tsx";
import { BillActionsDialog } from "@/components/bill-actions.tsx";
import { CoverPicker, TablePicker } from "@/components/tables.tsx";
import { BillStrip, ReleaseDialog, StartBillPanel } from "@/components/bills.tsx";
import { useBills } from "@/lib/use-bills.ts";
import { asksSomething, choicesFor, NO_CHOICES } from "@/lib/modifiers.ts";
import {
  displayName,
  matches,
  queryTerms,
  sortByName,
  subline,
  type Named,
  type Searchable,
} from "@/lib/menu.ts";

/**
 * The service screen (`08-pos-terminal-and-qr.md` § A).
 *
 * "Three panes: category rail → item grid → running cart. The cart is the
 * permanent right-hand column and is never navigated away from. Table/customer
 * context sits above the cart."
 *
 * That sentence is the entire layout, and it is a good one: a cashier's hand
 * lives on the right, and anything that moved the cart would cost a mis-tap at
 * the worst moment. Everything else on this screen is a sheet over it rather
 * than a place you go.
 *
 * The search box is the one addition to it. It sits above the grid rather than
 * inside it — a filter that scrolled away with its results would be a filter
 * you cannot clear — and it searches the whole menu rather than the selected
 * category, because a cashier hunting an item does not know which category
 * somebody filed it under.
 *
 * **Built from the written spec, not from a screen capture** — there are none
 * for the till (`spec/screens/README.md`). Tile size, rail width and the
 * grid's break points are judgement calls made for a 10" landscape terminal
 * and are the first things to revisit when captures arrive.
 */

export type MenuItem = Searchable & {
  id: string;
  sellPrice: string;
  soldOut: boolean;
  picUrl: string | null;
  labels: string[];
  discountPrice: string | null;
  categoryIds: string[];
  /**
   * The choices a cashier is meant to make before this reaches the station:
   * sizes and priced add-ons. `tagTemplateIds` is deliberately not here — a tag
   * is an unpriced station instruction (`packages/db/src/schema/menu.ts`), so
   * it changes no bill and need not stop a one-tap add.
   */
  specificationGroupIds: string[];
  toppingTemplateIds: string[];
};

export type Category = Named & { id: string; sort: number };

export function ServiceScreen() {
  const t = useTranslate();
  const { locale } = useI18n();
  const till = useTill();
  const queryClient = useQueryClient();

  /**
   * The rules the cart prices with.
   *
   * Fetched once and cached hard: the till runs the same engine the server
   * does, and it has to keep running when the network does not.
   */
  const config = useQuery({
    queryKey: ["pricing-config"],
    queryFn: () =>
      api<{
        settings: Record<string, unknown>;
        rules: unknown[];
        defaultServiceChargeRateBp: number;
      }>("/store/pricing-config"),
    staleTime: 30 * 60_000,
  });

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ store: { primaryCurrency: string } }>("/me"),
    staleTime: 30 * 60_000,
  });

  const context = useMemo(() => {
    if (!config.data || !me.data) return undefined;
    return {
      currency: me.data.store.primaryCurrency as "USD",
      diningStyle: "dine_in" as const,
      taxRules: config.data.rules as never,
      settings: config.data.settings as never,
      serviceChargeRateBp: config.data.defaultServiceChargeRateBp,
    };
  }, [config.data, me.data]);

  const { bills, book } = useBills();
  const cart = useCart(context, bills);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [discounting, setDiscounting] = useState(false);
  const [amending, setAmending] = useState<string | null>(null);
  const [billActions, setBillActions] = useState(false);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<MenuItem | null>(null);
  const [settling, setSettling] = useState(false);
  const [pickingTable, setPickingTable] = useState(false);
  /**
   * The bill the covers dialog was opened on, not just that it is open: the
   * cashier can tap another chip while it is up, and the count has to land on
   * the bill they asked about.
   */
  const [editingCovers, setEditingCovers] = useState<{
    key: string;
    table: { id: string; name: string };
    covers: number | null;
  } | null>(null);

  const menu = useQuery({
    queryKey: ["menu"],
    queryFn: () =>
      api<{ data: MenuItem[] }>("/store/items?size=200").then((response) => response.data),
    // The menu is cached hard and checked against `items/version`: a till on a
    // slow connection should not wait on it to take an order.
    staleTime: 5 * 60_000,
  });

  /**
   * A dish that just ran out greys out here, without a cashier reloading.
   *
   * The menu is cached for five minutes on purpose — a till should not wait on
   * it to take an order — which is exactly why 86ing has to arrive as an event
   * instead. Ringing something the kitchen cannot make is a conversation with a
   * guest, and five minutes is a long service.
   *
   * A refetch rather than a patch from the payload, like every other handler
   * here: an event says what changed, never what it changed to.
   */
  useEffect(() => {
    if (till.till.mode === "offline") return;
    const stream = subscribeToStoreEvents((event) => {
      if (event.type === "menu.availability") {
        void queryClient.invalidateQueries({ queryKey: ["menu"] });
      }
    });
    return () => stream.close();
  }, [till.till.mode, queryClient]);

  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: () =>
      api<{ data: Category[] }>("/store/categories?size=100").then((response) => response.data),
    staleTime: 5 * 60_000,
  });

  /**
   * The two modifier catalogues, which the sheet resolves an item against.
   *
   * `GET /store/items` names an item's groups by id only, so this is the other
   * half of a tile that asks a question. Cached like the menu and fetched once
   * for the whole service: a round trip when a sheet opens is a round trip
   * with a cashier waiting on it.
   *
   * Two queries rather than one, because they sit behind two different feature
   * codes: a merchant who bought sizes and not toppings must get their sizes
   * rather than a rejected pair.
   */
  const specifications = useQuery({
    queryKey: ["specification-groups"],
    queryFn: () =>
      api<{ data: SpecificationGroup[] }>("/store/specification-groups?size=200").then(
        (response) => response.data,
      ),
    staleTime: 5 * 60_000,
  });

  const toppings = useQuery({
    queryKey: ["topping-templates"],
    queryFn: () =>
      api<{ data: ToppingTemplate[] }>("/store/topping-templates?size=200").then(
        (response) => response.data,
      ),
    staleTime: 5 * 60_000,
  });

  /**
   * What the open sheet has to ask. Nothing at all when no sheet is open, and
   * nothing while a catalogue is missing — an item whose groups a downgraded
   * plan no longer serves is still an item a cashier has to be able to sell.
   */
  const choices = useMemo(
    () =>
      chosen
        ? choicesFor(chosen, specifications.data ?? [], toppings.data ?? [], locale)
        : NO_CHOICES,
    [chosen, specifications.data, toppings.data, locale],
  );

  const terms = useMemo(() => queryTerms(query), [query]);
  /**
   * Derived from the terms rather than from the raw box, so a lone space is
   * not a search — otherwise a thumb resting on the space bar blanks the rail.
   */
  const isSearching = terms.length > 0;

  /**
   * A query outranks the rail; it does not overwrite it. `categoryId` is never
   * written by the search path, so clearing the box restores the category the
   * cashier had chosen without anything having to remember it.
   */
  const items = useMemo(() => {
    const all = menu.data ?? [];
    // Results are alphabetical; browsing is not. The merchant's `sort` is a
    // considered order — what sells, what they want reached first — and it is
    // the right one for a category. It means nothing across a set of results
    // drawn from the whole menu, where A to Z is the only order a cashier can
    // predict well enough to aim at.
    if (isSearching)
      return sortByName(
        all.filter((item) => matches(item, terms, locale)),
        locale,
      );
    if (!categoryId) return all;
    return all.filter((item) => item.categoryIds.includes(categoryId));
  }, [menu.data, categoryId, terms, isSearching, locale]);

  /**
   * Category names by id, for the line under every tile's name.
   *
   * Localised through the same helper the rail uses, so a tile never names a
   * category differently from the button that would have led you to it.
   */
  const categoryNames = useMemo(() => {
    const byId = new Map<string, string>();
    for (const category of categories.data ?? []) {
      byId.set(category.id, displayName(category, locale));
    }
    return byId;
  }, [categories.data, locale]);

  /**
   * Where an item lives, shown on every tile.
   *
   * Searching the whole menu makes the rail stop answering "which category is
   * this?" — `burrata` returns five tiles that sit in four different places.
   * Browsing answers it for the grid but not for the tile: under All the rail
   * says nothing, and under one category it still does not say that the item
   * is filed in two more. Naming it on every tile costs the line the code is
   * already on and makes browsing and searching read the same. An item in
   * several categories names them all: picking one by array order would be
   * arbitrary.
   */
  function categoryLabel(item: MenuItem): string | undefined {
    const names = item.categoryIds.flatMap((id) => {
      const name = categoryNames.get(id);
      return name === undefined ? [] : [name];
    });
    return names.length > 0 ? names.join(" · ") : undefined;
  }

  /**
   * How many of each item are on the bill and not yet with the station.
   *
   * A quick tap opens no modal, so this badge is the only thing that tells a
   * cashier the tap landed. Without it the second tap is not "make that two",
   * it is "did the first one work?" — and the customer gets two.
   *
   * Unfired only, to agree with what a tap can actually change: `addOrIncrement`
   * will not touch a line the station already has.
   */
  const inCart = useMemo(() => {
    const byId = new Map<string, number>();
    for (const line of cart.lines) {
      // `cart.lines` rather than `cart.unfired`, which is refiltered on every
      // render and would make this memo miss every time.
      if (line.isFired) continue;
      const whole = Number(line.quantity.value) / 10 ** line.quantity.scale;
      byId.set(line.id, (byId.get(line.id) ?? 0) + whole);
    }
    return byId;
  }, [cart.lines]);

  /**
   * The fast path, and the one place that decides between adding and asking.
   *
   * Shared by a tile tap and the search box's Enter so the two cannot drift:
   * whatever a tap does to an item, the code typed into the box does too.
   */
  function addItem(item: MenuItem) {
    // The second lock. The tiles are disabled without a bill, and the search
    // box's Enter reaches this by another road — a gate on only one of them is
    // a gate that a keyboard walks around.
    if (!cart.bill) return;
    if (asksSomething(item)) {
      setChosen(item);
      return;
    }
    cart.addOrIncrement({
      key: crypto.randomUUID(),
      id: item.id,
      // The name off the tile, which is the name the cart and the ticket carry.
      name: displayName(item, locale),
      unitPrice: { amount: BigInt(item.sellPrice), currency: cart.currency as "USD" },
      quantity: toQuantity(1n),
      isFired: false,
    });
  }

  /**
   * Choosing a category is an explicit intent that ends the search. Without
   * this the rail would still be showing search results after the tap, which
   * reads as a button that does nothing.
   */
  function pickCategory(id: string | null) {
    setQuery("");
    setCategoryId(id);
  }

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setQuery("");
      return;
    }
    if (event.key !== "Enter") return;
    // A Khmer IME commits its candidate with Enter. Acting on that keystroke
    // would swallow the commit and open whatever happened to be filtered
    // mid-word. Only Enter is gated — the box must keep filtering as the
    // candidate changes, or typing Khmer feels dead.
    if (event.nativeEvent.isComposing) return;
    if (!isSearching) return;

    // The PLU workflow: type the code, Enter, done — regardless of how many
    // names the same digits also matched. Falling back to "the only result"
    // covers searching by name.
    const typed = terms.join(" ");
    const exact = items.find((item) => item.number?.toLowerCase() === typed);
    const target = exact ?? (items.length === 1 ? items[0] : undefined);
    // Sold out is a no-op: the tile is already disabled, and Enter must not be
    // a back door to promising a customer something the station cannot make.
    if (!target || target.soldOut) return;
    event.preventDefault();
    addItem(target);
    // Unlike a tap, which leaves the results up so the same tile can be tapped
    // again: a code has been typed and answered, and the next one starts empty.
    setQuery("");
  }

  return (
    <div className="flex h-full">
      {/* Category rail */}
      <nav className="scrollbar-chrome w-44 shrink-0 overflow-y-auto border-e border-chrome-line bg-chrome text-ink-inverse">
        <RailButton
          label={t("pos.menu.all")}
          isActive={isSearching || categoryId === null}
          onClick={() => pickCategory(null)}
        />
        {(categories.data ?? []).map((category) => (
          <RailButton
            key={category.id}
            label={displayName(category, locale)}
            isActive={!isSearching && categoryId === category.id}
            onClick={() => pickCategory(category.id)}
          />
        ))}
      </nav>

      {/* Item grid, under a search row that does not scroll with it.
          `min-h-0` on both this column and the scroller below is load-bearing:
          a flex child defaults to `min-height: auto` and would grow to its
          content instead of scrolling, taking the page with it. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Every bill this till has open, and the way to start another. */}
        <BillStrip
          book={book}
          onSwitch={(key) => void bills.setActive(key)}
          onNew={() => void bills.setActive(null)}
        />

        <div className="flex shrink-0 items-center gap-3 border-b border-line bg-surface px-3 py-2">
          <SearchInput
            size="lg"
            className="min-w-0 flex-1"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            onClear={() => setQuery("")}
            clearLabel={t("action.clear")}
            placeholder={t("pos.menu.searchPlaceholder")}
            aria-label={t("pos.menu.searchPlaceholder")}
            aria-controls="menu-grid"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {/* Mounted even when empty: a live region that appears at the same
              moment its text does is not announced. It carries the count and
              never the tile names — 200 items read aloud on the first
              keystroke is what putting `aria-live` on the grid would cost. */}
          <p
            role="status"
            aria-live="polite"
            className="shrink-0 text-sm tabular-nums text-ink-muted"
          >
            {isSearching
              ? t(items.length === 1 ? "pos.menu.result" : "pos.menu.results", {
                  count: items.length,
                })
              : ""}
          </p>
        </div>

        {/* The menu stays searchable with no bill open — a cashier answering
            "do you have the fish?" while the party is still standing needs it
            — so this sits above the grid rather than over it. */}
        {cart.bill ? null : (
          <StartBillPanel
            onChooseTable={() => setPickingTable(true)}
            onTakeAway={() => void bills.open(null, null)}
          />
        )}

        <div id="menu-grid" className="min-h-0 flex-1 overflow-y-auto p-3">
          {menu.isPending ? (
            <p className="text-sm text-ink-muted">{t("pos.menu.loading")}</p>
          ) : items.length === 0 && isSearching ? (
            <p className="text-sm text-ink-muted">{t("list.noMatches")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
              {items.map((item) => (
                <ItemTile
                  key={item.id}
                  item={item}
                  locale={locale}
                  category={categoryLabel(item)}
                  count={inCart.get(item.id) ?? 0}
                  currency={cart.currency}
                  disabled={!cart.bill}
                  onAdd={() => addItem(item)}
                  onOpen={() => setChosen(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* The cart, which never moves */}
      <CartPane
        cart={cart}
        onSettle={() => setSettling(true)}
        onDiscount={() => setDiscounting(true)}
        onAmendLine={(itemId) => setAmending(itemId)}
        onBillActions={() => setBillActions(true)}
        onPickTable={() => setPickingTable(true)}
        onEditCovers={() =>
          cart.bill?.table
            ? setEditingCovers({
                key: cart.bill.key,
                table: cart.bill.table,
                covers: cart.bill.covers,
              })
            : undefined
        }
        onRelease={() => setReleasing(true)}
        syncState={till.state}
      />

      {chosen ? (
        <ItemSheet
          item={chosen}
          choices={choices}
          // The groups are not here yet rather than absent: adding at base
          // price under a sheet that never asked is the one outcome worse
          // than a moment's wait.
          loading={specifications.isPending || toppings.isPending}
          currency={cart.currency}
          onClose={() => setChosen(null)}
          onAdd={(line: CartLine) => {
            cart.add(line);
            setChosen(null);
            // A line in the cart means the next search is a fresh one. A
            // cancelled sheet does not clear: the cashier picked the wrong
            // tile and wants the same results back.
            setQuery("");
          }}
        />
      ) : null}

      {pickingTable ? (
        <TablePicker
          isOffline={till.till.mode === "offline"}
          onClose={() => setPickingTable(false)}
          onPick={(table, covers) => {
            void bills.open(table, covers);
            setPickingTable(false);
          }}
          onResume={(orderId) => {
            void bills.adopt(orderId);
            setPickingTable(false);
          }}
        />
      ) : null}

      {editingCovers ? (
        <CoverPicker
          table={editingCovers.table}
          current={editingCovers.covers}
          onClose={() => setEditingCovers(null)}
          onPick={(covers) => {
            void bills.setCovers(editingCovers.key, covers);
            setEditingCovers(null);
          }}
        />
      ) : null}

      {releasing && cart.bill ? (
        <ReleaseDialog
          bill={cart.bill}
          onClose={() => setReleasing(false)}
          onRelease={() => {
            void bills.release(cart.bill!.key);
            setReleasing(false);
          }}
        />
      ) : null}

      {settling ? <SettleDialog cart={cart} onClose={() => setSettling(false)} /> : null}

      {discounting ? <DiscountDialog cart={cart} onClose={() => setDiscounting(false)} /> : null}

      {amending ? (
        <LineActionsDialog cart={cart} itemId={amending} onClose={() => setAmending(null)} />
      ) : null}

      {billActions ? (
        <BillActionsDialog
          cart={cart}
          book={bills.book()}
          online={till.till.mode === "online"}
          onMerge={(sourceKeys) => void bills.merge(cart.bill!.key, sourceKeys)}
          onSplit={(itemIds) => void bills.split(cart.bill!.key, itemIds)}
          onClose={() => setBillActions(false)}
        />
      ) : null}
    </div>
  );
}

function RailButton({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Tall rows: this is a finger on a greasy screen, not a mouse.
      className={`relative w-full px-4 py-4 text-start text-sm ${
        isActive ? "bg-chrome-active font-medium text-ink-inverse" : "text-ink-inverse-muted"
      }`}
    >
      {isActive ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-1.5 start-0 w-0.5 rounded-pill bg-primary"
        />
      ) : null}
      {label}
    </button>
  );
}

/**
 * An item tile.
 *
 * § A: "Item tiles show name, price, image, sold-out state, inventory
 * remaining, sales rank badge, and promo/strikethrough pricing." Sold-out is
 * the one that has to be unmissable rather than merely present — a cashier who
 * taps it is about to promise a customer something the station cannot make.
 *
 * A tap adds one; tapping again raises that line rather than opening a second.
 * A hold opens the sheet. `disabled` is what keeps a sold-out tile out of all
 * of it: it suppresses the pointer events and the keyboard activation together.
 */
function ItemTile({
  item,
  locale,
  category,
  count,
  currency,
  disabled,
  onAdd,
  onOpen,
}: {
  item: MenuItem;
  locale: Locale;
  /**
   * Where this item lives, joined onto the end of the code's line. Undefined
   * while the category list is still loading, and for an item filed under
   * nothing the store still has.
   */
  category?: string | undefined;
  /** How many are already on the bill and unfired. */
  count: number;
  currency: string;
  /** No bill is open, so there is nothing to add this to. */
  disabled: boolean;
  onAdd: () => void;
  onOpen: () => void;
}) {
  const t = useTranslate();
  const press = usePress({ onTap: onAdd, onHold: onOpen });
  // The code a cashier types into the search box, so it has to be readable on
  // the tile that code just found.
  const meta = subline(item, category);

  return (
    <button
      type="button"
      disabled={item.soldOut || disabled}
      // Reusing sold-out's treatment on purpose: one visual language for "you
      // cannot tap this", and `disabled` suppresses the pointer and the
      // keyboard together.
      aria-label={disabled ? t("pos.bill.itemsBlocked") : undefined}
      {...press}
      // `select-none` and the callout suppression are the hold's: without them
      // a press long enough to open the sheet first highlights the item name.
      className={`relative flex min-h-24 select-none flex-col justify-between rounded-card border border-line bg-surface p-3 text-start [-webkit-touch-callout:none] ${
        item.soldOut || disabled ? "opacity-40" : "active:bg-primary/10"
      }`}
    >
      {count > 0 ? (
        <span
          aria-label={t("pos.menu.inCart", { count })}
          className="absolute end-2 top-2 flex size-6 items-center justify-center rounded-pill bg-primary text-xs font-semibold tabular-nums text-ink-on-primary"
        >
          {count}
        </span>
      ) : null}
      <span className={`flex flex-col gap-0.5 ${count > 0 ? "pe-7" : ""}`}>
        <span className="line-clamp-2 text-sm font-medium text-ink-body">
          {displayName(item, locale)}
        </span>
        {meta ? <span className="line-clamp-1 text-xs text-ink-muted">{meta}</span> : null}
      </span>
      <span className="mt-2 flex items-baseline gap-2">
        <span className="text-base font-semibold text-ink-body">
          {formatMoney(item.sellPrice, currency)}
        </span>
        {item.discountPrice ? (
          <span className="text-xs text-ink-muted line-through">
            {formatMoney(item.discountPrice, currency)}
          </span>
        ) : null}
      </span>
      {item.soldOut ? (
        <Badge tone="danger">{t("pos.menu.soldOut")}</Badge>
      ) : item.labels.length > 0 ? (
        <span className="mt-1 text-xs text-ink-muted">{item.labels[0]}</span>
      ) : null}
    </button>
  );
}
